<template>
	<div v-if="lot" class="max-w-xl shadow-xl rounded-lg">
		<div class="rounded-lg border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/40 p-4">
			<div class="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div>
					<p class="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Herkomst</p>
					<div class="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
						<template v-if="originLink">
							<span class="text-neutral-500">{{ originLink.key }}</span>
							<span>{{ originLink.label }}</span>
						</template>
						<template v-else-if="splittedFrom">
							<span class="text-neutral-500">{{ $t('common.lot-tracing.split-from') }}</span>
							<span>{{ $t('common.lot-tracing.lot-title', [splittedFrom.lotNumber]) }}</span>
						</template>
						<span v-else-if="mergedFromLotCount > 0" class="col-span-2">
							{{ $t('common.lot-tracing.merged-from-count', [mergedFromLotCount]) }}
						</span>
						<span v-else class="col-span-2 text-neutral-500">
							{{ loadingSummary ? $t('common.lot-tracing.summary-loading') : $t('common.lot-tracing.imported') }}
						</span>
					</div>
				</div>

				<div>
					<p class="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Lot</p>
					<div class="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
						<span class="text-neutral-500">Lotnummer</span>
						<span>{{ lot.lotNumber }}</span>
						<template v-if="lot.product">
							<span class="text-neutral-500">Product</span>
							<span>{{ lot.product.name }}<template v-if="lot.product.number"> ({{ lot.product.number }})</template></span>
						</template>
						<span class="text-neutral-500">Magazijn</span>
						<span>{{ lot.warehouse?.name ?? "—" }}</span>
						<span class="text-neutral-500">Gewicht</span>
						<span class="font-mono">{{ $numberFormat(lot.weight) }} kg</span>
						<span class="text-neutral-500">Resterend</span>
						<span class="font-mono">{{ $numberFormat(lot.availableWeight) }} kg</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { Lot, TraceSummary } from "@q-food/q-food";
import { cachedGetTraceSummary } from "~utils/TraceGraphCache.ts";
import { translate } from "~/plugins/I18n.ts";

const props = defineProps<{ params: { data?: Lot } }>();
const lot = computed(() => props.params?.data ?? null);

const summary = ref<TraceSummary | null>(null);
const loadingSummary = ref(true);

onMounted(async () => {
	const id = lot.value?.id;
	if (!id) {
		loadingSummary.value = false;
		return;
	}
	summary.value = await cachedGetTraceSummary(id).catch(() => null);
	loadingSummary.value = false;
});

const originLink = computed<{ key: string; label: string } | null>(() => {
	const origin = summary.value?.origin;
	if (!origin) return null;
	if (origin.incomeLot) {
		return {
			key: translate("common.lot-tracing.purchase-order-label"),
			label: origin.incomeLot.purchaseOrderRow.purchaseOrder.orderNumber,
		};
	}
	if (origin.productionStepResultLot) {
		return {
			key: translate("common.lot-tracing.production-order-label"),
			label: origin.productionStepResultLot.lot.productionOrderNumber ?? "",
		};
	}
	return null;
});
const splittedFrom = computed(() => summary.value?.splittedFromLot ?? null);
const mergedFromLotCount = computed(() => summary.value?.mergedFromLotCount ?? 0);
</script>
